import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { updateAffiliation, deleteAffiliation } from "../../api/api";
import { defaultPfpUrl, affiliationRoles } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { FormattedBody } from "../ui/formatted-body";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { OrgTabHeader, OrgErrorPanel, OrgEmptyState, OrgRequestCard } from "./orgTabShell";
import { cn } from "../../lib/utils";

const fieldInputClass =
  "w-full rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-foreground placeholder:text-ui-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background";

/**
 * @param {Object} props
 * @param {Object} props
 * @param {Object} props.user
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element}
 */
function ActiveAffiliationsTab({ user, refreshUser }) {
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [loading, setLoading] = useState(false);
  const roleDropdownRef = useRef(null);

  const activeAffiliations = user?.affiliations?.filter((aff) => aff.status === "active") || [];

  useEffect(() => {
    function handleClickOutside(event) {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
    }
    if (showRoleDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showRoleDropdown]);

  useEffect(() => {
    if (editingId) {
      setRoleSearch(editRole);
    }
  }, [editingId, editRole]);

  const filteredRoles = affiliationRoles.filter((role) =>
    role.toLowerCase().includes(roleSearch.toLowerCase())
  );

  const displayName = (u) => {
    if (!u) return "Unknown";
    if (u.first_name && u.last_name) return `${u.first_name} ${u.last_name}`.trim();
    return u.username || "User";
  };

  const initials = (n) => n.replace(/\s+/g, " ").trim().slice(0, 2).toUpperCase() || "U";

  /**
   * @param {Object} affiliation
   */
  const handleEdit = (affiliation) => {
    setEditingId(affiliation.id);
    const currentRole = affiliation.role || "";
    setEditRole(currentRole);
    setRoleSearch(currentRole);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditRole("");
    setRoleSearch("");
    setShowRoleDropdown(false);
    setError(null);
  };

  /**
   * @param {string} role
   */
  const handleRoleSelect = (role) => {
    setEditRole(role);
    setRoleSearch(role);
    setShowRoleDropdown(false);
  };

  /**
   * @param {string} value
   */
  const handleRoleInputChange = (value) => {
    setRoleSearch(value);
    setEditRole(value);
    setShowRoleDropdown(true);
  };

  /**
   * @param {Object} affiliation
   * @returns {Promise<void>}
   */
  const handleSave = async (affiliation) => {
    setLoading(true);
    setError(null);
    try {
      const result = await updateAffiliation(affiliation.id, { role: editRole });

      if (result.error) {
        setError(result.error || "Failed to update affiliation");
      } else {
        setEditingId(null);
        setEditRole("");
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (err) {
      setError("Network error while updating affiliation");
    } finally {
      setLoading(false);
    }
  };

  /**
   * @param {Object} affiliation
   * @returns {Promise<void>}
   */
  const handleRemove = async (affiliation) => {
    setLoading(true);
    setError(null);
    try {
      const result = await deleteAffiliation(affiliation.user_id, user.id);

      if (result.error) {
        setError(result.error || "Failed to remove affiliation");
      } else {
        setConfirmRemove(null);
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (err) {
      setError("Network error while removing affiliation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <OrgTabHeader
        title="Active affiliations"
        description="Members and volunteers linked to your organization. Update roles or remove access."
      />

      {error ? <OrgErrorPanel message={error} /> : null}

      {activeAffiliations.length === 0 ? (
        <OrgEmptyState
          title="No active members yet"
          subtitle="Send invitations from the New affiliation tab to grow your team."
        />
      ) : (
        <ul className="list-none space-y-4 p-0">
          {activeAffiliations.map((affiliation) => {
            const u = affiliation.user;
            const name = displayName(u);
            const pfp = u?.pfp_url || defaultPfpUrl;

            return (
              <li key={affiliation.id}>
                <OrgRequestCard>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                        <Avatar className="h-16 w-16 shrink-0 border-2 border-ui-border">
                          <AvatarImage
                            src={pfp}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = defaultPfpUrl;
                            }}
                          />
                          <AvatarFallback className="text-text-secondary">{initials(name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/profile/${affiliation.user_id}`}
                              className="text-lg font-semibold text-accent transition-colors hover:text-text-accent-hover"
                            >
                              {name}
                            </Link>
                            {affiliation.affiliation_type ? (
                              <Badge variant="secondary" className="font-normal capitalize">
                                {affiliation.affiliation_type}
                              </Badge>
                            ) : null}
                          </div>
                          {editingId !== affiliation.id && affiliation.role ? (
                            <p className="mt-1 text-sm text-text-secondary">
                              Role: <span className="font-medium text-text-primary">{affiliation.role}</span>
                            </p>
                          ) : null}
                        </div>
                      </div>

                      {editingId === affiliation.id ? (
                        <div className="mt-2 space-y-3 border-t border-ui-border pt-3">
                          <div>
                            <label className="mb-1 block text-sm font-medium text-text-primary" htmlFor={`role-${affiliation.id}`}>
                              Role
                            </label>
                            <div className="relative" ref={roleDropdownRef}>
                              <input
                                id={`role-${affiliation.id}`}
                                type="text"
                                placeholder="Search or enter a role"
                                value={roleSearch}
                                onChange={(e) => handleRoleInputChange(e.target.value)}
                                onFocus={() => setShowRoleDropdown(true)}
                                disabled={loading}
                                className={fieldInputClass}
                              />
                              {showRoleDropdown && filteredRoles.length > 0 ? (
                                <div
                                  className="list-scrollbar absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-ui-border bg-ui-card py-0.5 shadow-profile-md"
                                >
                                  {filteredRoles.map((role) => (
                                    <button
                                      key={role}
                                      type="button"
                                      onClick={() => handleRoleSelect(role)}
                                      className="w-full px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-ui-muted"
                                    >
                                      {role}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              onClick={() => handleSave(affiliation)}
                              disabled={loading}
                            >
                              {loading ? "Saving…" : "Save"}
                            </Button>
                            <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={loading}>
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setConfirmRemove(affiliation)}
                              disabled={loading}
                              className={cn("border-delete text-delete hover:bg-ui-muted hover:text-delete")}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {affiliation.description ? (
                            <FormattedBody as="p" className="mb-2 text-sm text-text-secondary">
                              {affiliation.description}
                            </FormattedBody>
                          ) : null}
                          {affiliation.created_at ? (
                            <p className="text-sm text-text-muted">
                              Joined {new Date(affiliation.created_at).toLocaleDateString()}
                            </p>
                          ) : null}
                        </>
                      )}
                    </div>

                    {editingId !== affiliation.id ? (
                      <div className="shrink-0 sm:ml-2">
                        <Button type="button" onClick={() => handleEdit(affiliation)}>
                          Edit
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </OrgRequestCard>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={!!confirmRemove}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmRemove(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
            <DialogDescription>
              {confirmRemove
                ? `This removes ${displayName(confirmRemove.user)} from your organization. You can send a new invitation later.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setConfirmRemove(null)} disabled={loading}>
              Keep member
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => confirmRemove && handleRemove(confirmRemove)}
              disabled={loading}
            >
              {loading ? "Removing…" : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ActiveAffiliationsTab;
